/**
 * BrandingSection — Händler-Markendesign für Kunden-E-Mails (Jun-2026, User-Wunsch).
 *
 * KI analysiert die Händler-Homepage (Logo, Farben, Absendername) und füllt das
 * Theme vor; der Händler kann alles überschreiben. Live-Vorschau zeigt eine
 * gebrandete Beispiel-Mail. Plattform-/Konto-Mails bleiben TwoWheels-gebrandet.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { Sparkles, Loader2, Palette, Eye, ChevronDown, Check, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import BranchOverrideToggle from "./BranchOverrideToggle";

export function BrandingSection({ currentUser }) {
    const [b, setB] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [collapsed, setCollapsed] = useState(true);
    const [sigPreview, setSigPreview] = useState("");
    const [sending, setSending] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");
    const [previewType, setPreviewType] = useState("termin");
    const saveTimer = useRef(null);

    useEffect(() => {
        api.get("/dealer/branding").then((r) => setB(r.data)).catch(() => setB({
            homepage_url: "", logo_url: "", primary_color: "#FFCC00", secondary_color: "#111827", sender_name: "", enabled: false,
        }));
    }, []);

    useEffect(() => () => clearTimeout(saveTimer.current), []);

    const refreshPreview = useCallback(async (data, type) => {
        try {
            const r = await api.post(`/dealer/branding/preview?type=${type || "termin"}`, {
                logo_url: data.logo_url, primary_color: data.primary_color,
                secondary_color: data.secondary_color, sender_name: data.sender_name,
                signature: data.signature,
            });
            setPreviewHtml(r.data.html || "");
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { if (b) refreshPreview(b, previewType); }, [b, previewType, refreshPreview]);

    // Live-Vorschau der zusammengesetzten Signatur (Standort + Bearbeiter).
    useEffect(() => {
        if (collapsed) return;
        api.get("/dealer/signature-preview").then((r) => setSigPreview(r.data?.html || "")).catch(() => { });
    }, [collapsed, savedAt]);

    // Autospeichern: jede Änderung wird debounced (900 ms) gespeichert — kein
    // expliziter Speichern-Button mehr nötig.
    const doSave = useCallback(async (data) => {
        setSaving(true);
        try {
            const r = await api.put("/dealer/branding", {
                homepage_url: data.homepage_url, logo_url: data.logo_url,
                primary_color: data.primary_color, secondary_color: data.secondary_color,
                sender_name: data.sender_name, signature: data.signature, enabled: data.enabled,
            });
            setB((prev) => ({ ...prev, ...r.data }));
            setSavedAt(Date.now());
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        }
        setSaving(false);
    }, []);

    const scheduleSave = useCallback((data) => {
        clearTimeout(saveTimer.current);
        setSaving(true);
        saveTimer.current = setTimeout(() => doSave(data), 900);
    }, [doSave]);

    const set = (k, v) => setB((p) => {
        const next = { ...p, [k]: v };
        scheduleSave(next);
        return next;
    });

    const analyze = async () => {
        if (!b?.homepage_url) { toast.error("Bitte Homepage-URL eingeben"); return; }
        setAnalyzing(true);
        try {
            const r = await api.post("/dealer/branding/analyze", { homepage_url: b.homepage_url });
            const s = r.data.suggestion || {};
            setB((p) => {
                const next = {
                    ...p,
                    logo_url: s.logo_url || p.logo_url,
                    primary_color: s.primary_color || p.primary_color,
                    secondary_color: s.secondary_color || p.secondary_color,
                    sender_name: s.sender_name || p.sender_name,
                    signature: s.signature || p.signature,
                };
                scheduleSave(next);
                return next;
            });
            toast.success("KI-Analyse übernommen — automatisch gespeichert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Analyse fehlgeschlagen");
        }
        setAnalyzing(false);
    };

    if (!b) return <div className="py-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

    const sendTestMail = async () => {
        setSending(true);
        try {
            const r = await api.post("/users/me/send-signature-preview");
            toast.success(`Beispiel-Mail an ${r.data?.sent_to || "deine Adresse"} gesendet.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vorschau-Mail fehlgeschlagen");
        }
        setSending(false);
    };

    return (
        <div className="border-b border-gray-200" data-testid="branding-section">
            <div className="flex items-stretch bg-gray-50 hover:bg-gray-100 border-b border-gray-200">
            <button
                type="button"
                onClick={() => setCollapsed((v) => !v)}
                className="flex-1 min-w-0 px-5 py-3 flex items-center gap-3 text-left"
                data-testid="branding-section-toggle"
                aria-expanded={!collapsed}
            >
                <Palette className="w-4 h-4 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-[#111827]">Markendesign (Kunden-E-Mails)</div>
                    <div className="text-[11px] text-gray-500">Logo, Farben, Absendername &amp; Signatur für Kunden-E-Mails.</div>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold shrink-0" data-testid="branding-save-status">
                    {saving ? (
                        <span className="text-gray-400 inline-flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Speichert…</span>
                    ) : savedAt ? (
                        <span className="text-emerald-600 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Gespeichert</span>
                    ) : (
                        <span className="text-gray-400">Autospeichern</span>
                    )}
                </span>
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${!collapsed ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
            </button>
            <div className="flex items-center pr-4 pl-2 shrink-0">
                <BranchOverrideToggle
                    area="branding"
                    currentUser={currentUser}
                    infoText="Markendesign (E-Mail-Logo, Farben, Absendername & Signatur) des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                    testId="branding-branch-override"
                />
            </div>
            </div>

            {!collapsed && (
            <div className="p-5 space-y-5" data-testid="branding-section-body">
                <p className="text-sm text-gray-500">
                    E-Mails an deine Kunden erscheinen im Design deines Betriebs (Logo + Farben). Plattform- und Konto-Mails bleiben neutral. Änderungen werden automatisch gespeichert.
                </p>

            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-sm px-3 py-2.5">
                <div>
                    <div className="text-sm font-bold">Händler-Branding aktiv</div>
                    <div className="text-xs text-gray-500">Wenn aus, werden Kunden-Mails im TwoWheels-Standard versendet.</div>
                </div>
                <Switch checked={!!b.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="branding-enabled" />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 items-end">
                <div className="flex-1 w-full">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Homepage-URL</label>
                    <Input value={b.homepage_url || ""} onChange={(e) => set("homepage_url", e.target.value)} placeholder="https://www.mein-betrieb.de" className="rounded-sm mt-1" data-testid="branding-homepage" />
                </div>
                <Button type="button" onClick={analyze} disabled={analyzing} className="bg-[#111827] hover:bg-black text-white rounded-sm font-bold whitespace-nowrap" data-testid="branding-analyze">
                    {analyzing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />} Mit KI analysieren
                </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Absendername</label>
                    <Input value={b.sender_name || ""} onChange={(e) => set("sender_name", e.target.value)} placeholder="Mein Betrieb GmbH" className="rounded-sm mt-1" data-testid="branding-sender" />
                </div>
                <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Logo-URL</label>
                    <Input value={b.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://…/logo.png" className="rounded-sm mt-1" data-testid="branding-logo" />
                </div>
                <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Primärfarbe (Header)</label>
                    <div className="flex gap-2 mt-1">
                        <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(b.primary_color || "") ? b.primary_color : "#FFCC00"} onChange={(e) => set("primary_color", e.target.value)} className="h-9 w-12 rounded-sm border border-gray-300" data-testid="branding-primary-color" />
                        <Input value={b.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} className="rounded-sm" />
                    </div>
                </div>
                <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Akzentfarbe</label>
                    <div className="flex gap-2 mt-1">
                        <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(b.secondary_color || "") ? b.secondary_color : "#111827"} onChange={(e) => set("secondary_color", e.target.value)} className="h-9 w-12 rounded-sm border border-gray-300" data-testid="branding-secondary-color" />
                        <Input value={b.secondary_color || ""} onChange={(e) => set("secondary_color", e.target.value)} className="rounded-sm" />
                    </div>
                </div>
            </div>

            <div data-testid="signature-preview-wrap">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                    <Eye className="w-3.5 h-3.5" /> Signatur-Vorschau (Standort + Bearbeiter)
                </div>
                <div
                    className="border border-gray-200 rounded-sm p-5 bg-white text-sm leading-relaxed [&_*]:!text-gray-400 [&_a]:!text-gray-400 [&_div]:!text-[13px] [&_a]:!text-[13px] [&_td]:!text-[13px] [&_div:first-child]:!text-[15px]"
                    style={{ zoom: 1.2 }}
                    data-testid="signature-preview"
                    dangerouslySetInnerHTML={{ __html: sigPreview || "<span style='color:#9CA3AF'>Noch keine Signatur hinterlegt.</span>" }}
                />
                <p className="text-[11px] text-gray-400 mt-1">
                    Die Signatur wird automatisch aus den Stammdaten dieses Standortes zusammengesetzt: zuerst der eingeloggte Bearbeiter (Name, Durchwahl, E-Mail, Profilbild), darunter Firmenname, Anschrift, Sammelrufnummer, Sammel-E-Mail &amp; Öffnungszeiten. Es ist kein manueller Signatur-Text mehr nötig.
                </p>
            </div>

            <div>
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-600">
                        <Eye className="w-3.5 h-3.5" /> Live-Vorschau
                    </div>
                    <div className="flex items-center gap-1 bg-gray-100 rounded-sm p-0.5" data-testid="branding-preview-toggle">
                        {[
                            { key: "termin", label: "Termin" },
                            { key: "rechnung", label: "Rechnung" },
                            { key: "erinnerung", label: "Erinnerung" },
                        ].map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setPreviewType(t.key)}
                                className={`text-xs font-bold px-2.5 py-1 rounded-sm transition-colors ${
                                    previewType === t.key ? "bg-[#111827] text-white" : "text-gray-600 hover:bg-gray-200"
                                }`}
                                data-testid={`branding-preview-type-${t.key}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="border border-gray-200 rounded-sm overflow-hidden bg-[#F8F9FA]" data-testid="branding-preview">
                    <iframe title="E-Mail-Vorschau" srcDoc={previewHtml} className="w-full" style={{ height: 420, border: 0 }} />
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap bg-gray-50 border border-gray-200 rounded-sm px-3 py-2.5">
                    <div className="text-xs text-gray-600">
                        Beispiel-Signatur an deine eigene Adresse senden, um Avatar &amp; Header-Branding im echten Mailclient zu prüfen.
                    </div>
                    <Button type="button" onClick={sendTestMail} disabled={sending} className="bg-[#111827] hover:bg-black text-white rounded-sm font-bold whitespace-nowrap" data-testid="branding-send-test-mail">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Test-Mail an mich senden
                    </Button>
                </div>
            </div>
            </div>
            )}
        </div>
    );
}
